/**
 * Locally-administered MAC addresses for the interfaces we synthesise.
 *
 * The old scheme varied ONLY the last byte of a fixed base, which made the
 * address space 256 wide and, worse, identical for every tenant: every
 * opsanio cluster minted 06:99:C6:00:00:01 for its first appliance. Duplicate
 * MACs on a shared fabric make a switch see one address on two ports and flap
 * between them, and they also make most's by-MAC lookup - which indexes every
 * MAC in a cluster document - resolve a node to the WRONG CLUSTER.
 *
 * Bytes 3 and 4 of the base were unused zeros, so the tenant id goes there:
 *
 *     06:99:C6:<tenant hi>:<tenant lo>:<host offset>
 *
 * The tenant id is the VPC vlan (`networks.vpc.vlan`), which is unique per
 * customer estate and already flows through the values.
 */

/** Widest host offset the last byte can carry. */
export const MAX_HOST_OFFSET = 0xff;

/** Widest tenant id bytes 3-4 can carry. */
export const MAX_TENANT_ID = 0xffff;

export const calculatePrivateMacAddresses = (
  baseMacs: string[],
  hostOffset: number,
  tenantId?: number | null,
): string[] => {
  // Previously `& 0xff`, which wrapped in silence: appliance 256 was handed
  // appliance 0's address. An estate that large is implausible, but a silent
  // duplicate MAC is the single hardest fault to see from either end.
  if (!Number.isInteger(hostOffset) || hostOffset < 0) {
    throw new Error(
      `host offset must be a non-negative integer, got ${hostOffset}`,
    );
  }
  if (hostOffset > MAX_HOST_OFFSET) {
    throw new Error(
      `host offset ${hostOffset} exceeds ${MAX_HOST_OFFSET}; the last MAC byte cannot carry it`,
    );
  }

  // Absent or zero leaves bytes 3-4 at zero, i.e. exactly the historical
  // address. A site whose VPC has no tenant id is therefore unchanged rather
  // than silently renumbered onto a different scheme.
  let hi = 0;
  let lo = 0;
  if (tenantId !== undefined && tenantId !== null && tenantId !== 0) {
    if (
      !Number.isInteger(tenantId) ||
      tenantId < 0 ||
      tenantId > MAX_TENANT_ID
    ) {
      throw new Error(
        `tenant id ${tenantId} does not fit in 16 bits; MAC scoping needs 0..${MAX_TENANT_ID}`,
      );
    }
    hi = (tenantId >> 8) & 0xff;
    lo = tenantId & 0xff;
  }

  return baseMacs.map((mac) => {
    const bytes = mac.split(":").map((b) => parseInt(b, 16));
    bytes[3] = hi;
    bytes[4] = lo;
    bytes[5] = (bytes[5] + hostOffset) & 0xff;
    return bytes
      .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
      .join(":");
  });
};
